if (!customElements.get('deals-carousel')) {
  customElements.define('deals-carousel', class DealsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.track = this.querySelector('[data-carousel-track]');
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.previousButton = this.querySelector('[data-carousel-previous]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.drawer = this.querySelector('[data-deal-drawer]');
      this.drawerOverlay = this.querySelector('[data-deal-drawer-overlay]');
      this.drawerTitle = this.querySelector('[data-deal-drawer-title]');
      this.closeButton = this.querySelector('[data-deal-drawer-close]');
      this.nativeForm = this.drawer?.querySelector('.deal-drawer__native-form');
      this.formSuccess = this.drawer?.querySelector('[data-deal-form-success]');
      this.formError = this.drawer?.querySelector('[data-deal-form-error]');
      this.formDealInput = this.drawer?.querySelector('[data-deal-form-deal]');
      this.stickyRail = this.querySelector('[data-deal-sticky-rail]');
      this.dealsGroup = this.closest('.advisory-group');
      this.isAnimating = false;
      this.mobileLoopInitialized = false;
      this.mobileCarouselQuery = window.matchMedia('(max-width: 900px)');

      this.previousButton?.addEventListener('click', () => this.move('previous'));
      this.nextButton?.addEventListener('click', () => this.move('next'));
      this.querySelectorAll('[data-deal-request]').forEach((button) => {
        button.addEventListener('click', () => this.openDrawer(button));
      });
      this.closeButton?.addEventListener('click', () => this.closeDrawer());
      this.drawerOverlay?.addEventListener('click', () => this.closeDrawer());
      this.onDrawerKeydown = (event) => {
        if (!this.drawer?.classList.contains('is-open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeDrawer();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
          .filter((element) => !element.disabled && element.getClientRects().length);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', this.onDrawerKeydown);
      this.nativeForm?.addEventListener('submit', () => {
        try {
          sessionStorage.setItem('dealTeaserTitle', this.formDealInput?.value || '');
        } catch (error) {
          // The form still submits when session storage is unavailable.
        }
      });

      if (this.drawer) document.body.append(this.drawer);
      if (this.stickyRail) document.body.append(this.stickyRail);
      this.setupStickyRail();

      this.onMobileCarouselScroll = () => {
        if (!this.mobileCarouselQuery.matches || !this.mobileLoopInitialized || this.isNormalizingMobileLoop) return;
        window.clearTimeout(this.mobileScrollTimer);
        this.mobileScrollTimer = window.setTimeout(() => this.normalizeMobileLoop(), 160);
      };
      this.viewport?.addEventListener('scroll', this.onMobileCarouselScroll, { passive: true });

      this.onCarouselBreakpointChange = () => {
        window.clearTimeout(this.mobileScrollTimer);
        if (this.viewport) this.viewport.scrollLeft = 0;
        if (this.track) {
          this.track.style.transition = '';
          this.track.style.transform = '';
        }
        this.isAnimating = false;
        this.mobileLoopInitialized = false;
        if (this.mobileCarouselQuery.matches) {
          requestAnimationFrame(() => requestAnimationFrame(() => this.initializeMobileLoop()));
        }
      };
      if (this.mobileCarouselQuery.addEventListener) {
        this.mobileCarouselQuery.addEventListener('change', this.onCarouselBreakpointChange);
      } else {
        this.mobileCarouselQuery.addListener(this.onCarouselBreakpointChange);
      }
      if (this.mobileCarouselQuery.matches) {
        requestAnimationFrame(() => requestAnimationFrame(() => this.initializeMobileLoop()));
      }
      this.restoreNativeFormState();
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        window.removeEventListener('scroll', this.queueStickyRailUpdate);
        window.removeEventListener('resize', this.queueStickyRailUpdate);
        document.removeEventListener('keydown', this.onDrawerKeydown);
        this.viewport?.removeEventListener('scroll', this.onMobileCarouselScroll);
        window.clearTimeout(this.mobileScrollTimer);
        if (this.mobileCarouselQuery?.removeEventListener) {
          this.mobileCarouselQuery.removeEventListener('change', this.onCarouselBreakpointChange);
        } else {
          this.mobileCarouselQuery?.removeListener(this.onCarouselBreakpointChange);
        }
        this.stickyRailObserver?.disconnect();
        if (this.stickyRailFrame) cancelAnimationFrame(this.stickyRailFrame);
        this.stickyRail?.remove();
        this.drawer?.remove();
      });
    }

    initializeMobileLoop() {
      if (!this.mobileCarouselQuery.matches || this.mobileLoopInitialized) return;
      const slides = this.getSlides();
      const step = this.getStep();
      if (!this.viewport || !this.track || slides.length < 2 || !step) return;

      this.isNormalizingMobileLoop = true;
      this.track.prepend(slides[slides.length - 1]);
      this.viewport.scrollLeft = step;
      this.mobileLoopInitialized = true;
      requestAnimationFrame(() => {
        this.isNormalizingMobileLoop = false;
      });
    }

    normalizeMobileLoop() {
      if (!this.mobileCarouselQuery.matches || !this.mobileLoopInitialized || !this.viewport || !this.track) return;
      const step = this.getStep();
      if (!step) return;

      let scrollLeft = this.viewport.scrollLeft;
      let didReorder = false;
      this.isNormalizingMobileLoop = true;

      while (scrollLeft < step * 0.5) {
        const slides = this.getSlides();
        this.track.prepend(slides[slides.length - 1]);
        scrollLeft += step;
        didReorder = true;
      }

      while (scrollLeft > step * 1.5) {
        const slides = this.getSlides();
        this.track.append(slides[0]);
        scrollLeft -= step;
        didReorder = true;
      }

      if (didReorder) {
        const previousSnapType = this.viewport.style.scrollSnapType;
        this.viewport.style.scrollSnapType = 'none';
        this.viewport.scrollLeft = scrollLeft;
        this.viewport.getBoundingClientRect();
        this.viewport.style.scrollSnapType = previousSnapType;
      }

      requestAnimationFrame(() => {
        this.isNormalizingMobileLoop = false;
      });
    }

    setupStickyRail() {
      if (!this.stickyRail || !this.dealsGroup) return;

      this.updateStickyRail = () => {
        this.stickyRailFrame = null;
        const navigationSection = document.querySelector('.section-navigation-bar');
        const advisoryButton = document.querySelector('[data-homepage-navigation] [data-audience="advisory"]');
        const navigationBottom = navigationSection ? navigationSection.getBoundingClientRect().bottom : 0;
        const hasPassedDeals = this.dealsGroup.getBoundingClientRect().bottom <= navigationBottom;
        const advisoryIsActive = !advisoryButton || advisoryButton.classList.contains('active');
        const shouldShow = hasPassedDeals && advisoryIsActive;

        this.stickyRail.style.setProperty('--deal-sticky-top', `${Math.max(0, navigationBottom)}px`);
        this.stickyRail.classList.toggle('is-visible', shouldShow);
        this.stickyRail.setAttribute('aria-hidden', String(!shouldShow));
        this.stickyRail.inert = !shouldShow;
      };

      this.queueStickyRailUpdate = () => {
        if (this.stickyRailFrame) return;
        this.stickyRailFrame = requestAnimationFrame(this.updateStickyRail);
      };

      window.addEventListener('scroll', this.queueStickyRailUpdate, { passive: true });
      window.addEventListener('resize', this.queueStickyRailUpdate);

      if ('MutationObserver' in window) {
        this.stickyRailObserver = new MutationObserver(this.queueStickyRailUpdate);
        const navigation = document.querySelector('[data-homepage-navigation]');
        const audiencePanel = this.closest('[data-homepage-audience-panel]');
        if (navigation) this.stickyRailObserver.observe(navigation, { attributes: true, attributeFilter: ['class'], subtree: true });
        if (audiencePanel) this.stickyRailObserver.observe(audiencePanel, { attributes: true, attributeFilter: ['aria-hidden'] });
      }

      this.queueStickyRailUpdate();
    }

    getSlides() {
      return Array.from(this.track?.querySelectorAll('[data-carousel-slide]') || []);
    }

    getStep() {
      const firstSlide = this.getSlides()[0];
      if (!firstSlide || !this.track) return 0;
      const styles = window.getComputedStyle(this.track);
      return firstSlide.getBoundingClientRect().width + (parseFloat(styles.columnGap || styles.gap) || 0);
    }

    move(direction) {
      const slides = this.getSlides();
      if (!this.track || slides.length < 2 || this.isAnimating) return;

      const step = this.getStep();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.mobileCarouselQuery.matches) {
        this.moveMobile(direction, reduceMotion);
        return;
      }

      //const slidesPerMove = Math.min(2, slides.length - 1);
      const slidesPerMove = 1;
      const distance = step * slidesPerMove;
      this.isAnimating = true;

      if (direction === 'previous') {
        this.track.prepend(...slides.slice(-slidesPerMove));
        this.track.style.transition = 'none';
        this.track.style.transform = `translate3d(-${distance}px, 0, 0)`;
        this.track.getBoundingClientRect();
        this.track.style.transition = reduceMotion ? 'none' : '';
        requestAnimationFrame(() => {
          this.track.style.transform = 'translate3d(0, 0, 0)';
          this.finishMove(reduceMotion);
        });
        return;
      }

      this.track.style.transition = reduceMotion ? 'none' : '';
      this.track.style.transform = `translate3d(-${distance}px, 0, 0)`;
      this.finishMove(reduceMotion, () => {
        this.track.append(...slides.slice(0, slidesPerMove));
        this.track.style.transition = 'none';
        this.track.style.transform = 'translate3d(0, 0, 0)';
        this.track.getBoundingClientRect();
        this.track.style.transition = '';
      });
    }

    finishMove(reduceMotion, callback) {
      if (reduceMotion) {
        callback?.();
        this.isAnimating = false;
        return;
      }

      const onEnd = (event) => {
        if (event.target !== this.track || event.propertyName !== 'transform') return;
        this.track.removeEventListener('transitionend', onEnd);
        callback?.();
        this.track.style.transition = '';
        this.isAnimating = false;
      };
      this.track.addEventListener('transitionend', onEnd);
    }

    moveMobile(direction, reduceMotion) {
      if (!this.viewport || !this.track) return;
      if (!this.mobileLoopInitialized) this.initializeMobileLoop();

      const step = this.getStep();
      if (!step) return;

      if (direction === 'previous' && this.viewport.scrollLeft < step * 0.75) {
        const slides = this.getSlides();
        const currentScrollLeft = this.viewport.scrollLeft;
        const previousSnapType = this.viewport.style.scrollSnapType;
        this.isNormalizingMobileLoop = true;
        this.viewport.style.scrollSnapType = 'none';
        this.track.prepend(slides[slides.length - 1]);
        this.viewport.scrollLeft = currentScrollLeft + step;
        this.viewport.getBoundingClientRect();
        this.viewport.style.scrollSnapType = previousSnapType;
        this.isNormalizingMobileLoop = false;
      }

      requestAnimationFrame(() => {
        this.viewport.scrollBy({
          left: direction === 'previous' ? -step : step,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      });
    }

    openDrawer(button, focusTarget) {
      if (!this.drawer) return;
      if (this.drawerTitle && button?.dataset.dealTitle) this.drawerTitle.textContent = button.dataset.dealTitle;
      if (button?.dataset.dealTitle && this.formDealInput) this.formDealInput.value = button.dataset.dealTitle;
      this.lastTrigger = button || document.activeElement;
      this.drawer.classList.remove('is-open', 'is-closing');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.inert = false;
      document.documentElement.classList.add('deal-drawer-open');
      const panel = this.drawer.querySelector('.deal-drawer__panel');
      panel?.getBoundingClientRect();
      this.drawerOpenFrame = requestAnimationFrame(() => {
        if (!this.drawer || this.drawer.classList.contains('is-closing')) return;
        this.drawer.classList.add('is-open');
        (focusTarget || this.closeButton)?.focus({ preventScroll: true });
      });
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.classList.contains('is-closing')) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let hasClosed = false;
      const finishClose = () => {
        if (hasClosed) return;
        hasClosed = true;
        this.drawer.classList.remove('is-open', 'is-closing');
        this.drawer.setAttribute('aria-hidden', 'true');
        this.drawer.inert = true;
        document.documentElement.classList.remove('deal-drawer-open');
        this.lastTrigger?.focus();
      };

      if (reduceMotion) {
        finishClose();
        return;
      }

      if (this.drawerOpenFrame) cancelAnimationFrame(this.drawerOpenFrame);
      this.drawer.classList.add('is-closing');
      this.drawer.classList.remove('is-open');
      const panel = this.drawer.querySelector('.deal-drawer__panel');
      if (!panel) {
        finishClose();
        return;
      }
      const onTransitionEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        panel.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      };
      panel.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(finishClose, 450);
    }

    restoreNativeFormState() {
      if (!this.drawer || !this.nativeForm) return;
      const parameters = new URLSearchParams(window.location.search);
      const isDrawerFormReturn = window.location.hash === `#${this.nativeForm.id}`;
      if (!isDrawerFormReturn) return;

      const isSuccessful = parameters.get('contact_posted') === 'true';

      if (isSuccessful) {
        this.formSuccess.hidden = false;
        try {
          const submittedDealTitle = sessionStorage.getItem('dealTeaserTitle');
          if (submittedDealTitle && this.drawerTitle) this.drawerTitle.textContent = submittedDealTitle;
          sessionStorage.removeItem('dealTeaserTitle');
        } catch (error) {
          // The success state does not depend on session storage.
        }
        this.openDrawer(null, this.formSuccess);
        return;
      }

      if (this.formError) this.openDrawer(null, this.formError);
    }
  });
}
